/*
  Yard Automation Pros motion layer (GSAP + ScrollTrigger, both shipped in theme assets).
  Loaded with defer after gsap.min.js and ScrollTrigger.min.js.
  Every animation is skipped for prefers-reduced-motion, and content stays visible
  if GSAP is missing.
*/
(function () {
  if (window.__yapMotion) return;
  window.__yapMotion = true;

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var gsap = window.gsap;
  var ST = window.ScrollTrigger;
  if (!gsap || !ST || reduce) {
    document.documentElement.classList.add("yap-no-motion");
    return;
  }
  gsap.registerPlugin(ST);

  var mm = gsap.matchMedia();
  /* PERF: phones and tablets get static content (no reveals, clips or refresh loops);
     scroll-linked motion there caused heavy style recalcs and scroll lag. */
  var still = window.matchMedia("(max-width: 989px), (hover: none)").matches;
  var registry = new Map();

  function headerOffset() {
    var h = document.querySelector(".site-header");
    return h ? Math.round(h.getBoundingClientRect().height) : 0;
  }

  /* One-time reveals use IntersectionObserver so late layout shifts
     (lazy images, sliders initialising) cannot strand hidden content. */
  function onView(el, cb) {
    if (!el) return;
    if (!("IntersectionObserver" in window)) { cb(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { io.disconnect(); cb(); }
      });
    }, { rootMargin: "0px 0px -10% 0px" });
    io.observe(el);
  }

  function revealIn(ctx, trigger, targets, vars) {
    if (still || !targets || !targets.length) return;
    ctx.add(function () { gsap.set(targets, { autoAlpha: 0, y: vars.y || 32 }); });
    onView(trigger, function () {
      ctx.add(function () {
        gsap.to(targets, {
          autoAlpha: 1, y: 0,
          duration: vars.duration || 0.8,
          ease: "power3.out",
          stagger: vars.stagger || 0.1,
          clearProps: "transform,opacity,visibility"
        });
      });
    });
  }

  /* ---------- Product grids: staggered entrance ---------- */
  function initProductGrids(root) {
    var items = (root || document).querySelectorAll(".productgrid--item:not([data-yap-seen])");
    if (!items.length) return;
    items.forEach(function (el) { el.setAttribute("data-yap-seen", ""); });
    var groups = new Map();
    items.forEach(function (el) {
      var parent = el.parentElement;
      if (!groups.has(parent)) groups.set(parent, []);
      groups.get(parent).push(el);
    });
    var ctx = gsap.context(function () {});
    groups.forEach(function (list, parent) {
      revealIn(ctx, parent, list, { y: 28, duration: 0.7, stagger: 0.08 });
    });
  }

  /* ---------- Generic reveal for [data-yap-reveal] ---------- */
  function reveals(section, ctxList) {
    var els = section.querySelectorAll("[data-yap-reveal]");
    if (!els.length) return;
    var ctx = gsap.context(function () {}, section);
    revealIn(ctx, section, els, {});
    ctxList.push(ctx);
  }

  /* ---------- Time back: clip reveal + gentle parallax ---------- */
  function initTimeBack(section, ctxList) {
    reveals(section, ctxList);
    if (still) return;
    var ctx = gsap.context(function () {
      var cards = section.querySelectorAll(".ytb-card");
      cards.forEach(function (card, i) {
        var media = card.querySelector(".ytb-card__media");
        gsap.set(media, { clipPath: "inset(18% 12% 18% 12% round 20px)" });
        onView(card, function () {
          ctx.add(function () {
            gsap.to(media, { clipPath: "inset(0% 0% 0% 0% round 20px)", duration: 1.1, ease: "power3.out", delay: i * 0.12 });
          });
        });
      });
    }, section);
    ctxList.push(ctx);
  }

  /* ---------- Brand panels: stagger in, then CSS handles hover ---------- */
  function initBrandPanels(section, ctxList) {
    reveals(section, ctxList);
    var ctx = gsap.context(function () {}, section);
    revealIn(ctx, section.querySelector(".ybp-track"), section.querySelectorAll(".ybp-panel"), { y: 60, duration: 0.9, stagger: 0.09 });
    ctxList.push(ctx);

    var panels = section.querySelectorAll(".ybp-panel");
    function activate(p) {
      panels.forEach(function (x) {
        var on = x === p;
        x.classList.toggle("is-active", on);
        x.setAttribute("aria-expanded", on ? "true" : "false");
      });
    }
    panels.forEach(function (p) {
      p.addEventListener("mouseenter", function () { if (window.matchMedia("(min-width: 990px)").matches) activate(p); });
      p.addEventListener("focusin", function () { if (p.matches(":focus-visible")) activate(p); });
      // Mobile: first tap opens a collapsed brand, a tap on the open card follows the link.
      p.addEventListener("click", function (e) {
        if (window.matchMedia("(max-width: 989px)").matches && !p.classList.contains("is-active")) {
          e.preventDefault();
          activate(p);
          setTimeout(function () { p.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, 350);
        }
      });
    });
  }

  /* ---------- Season scroll: pinned story + drifting photo columns ---------- */
  function initSeasonScroll(section, ctxList) {
    reveals(section, ctxList);
    var chapters = section.querySelectorAll(".yss-chapter");
    var groups = section.querySelectorAll(".yss-group");
    var bar = section.querySelector(".yss-progress__bar");
    var count = section.querySelector("[data-yss-count]");
    if (!chapters.length || !groups.length) return;

    function setChapter(i) {
      chapters.forEach(function (c, n) { c.classList.toggle("is-active", n === i); });
      groups.forEach(function (g, n) { g.classList.toggle("is-active", n === i); });
      if (count) count.textContent = String(i + 1).padStart(2, "0");
    }
    setChapter(0);

    mm.add("(min-width: 990px)", function () {
      var ctx = gsap.context(function () {
        var stage = section.querySelector(".yss-stage");
        var aside = section.querySelector(".yss-aside");

        ST.create({
          trigger: stage,
          start: function () { return "top top+=" + (headerOffset() + 24); },
          end: function () { return "bottom bottom-=" + 24; },
          pin: aside,
          pinSpacing: false,
          invalidateOnRefresh: true
        });

        if (bar) {
          gsap.fromTo(bar, { scaleY: 0 }, {
            scaleY: 1,
            ease: "none",
            scrollTrigger: { trigger: stage, start: "top center", end: "bottom center", scrub: 0.4 }
          });
        }

        groups.forEach(function (g, i) {
          ST.create({
            trigger: g,
            start: "top center",
            end: "bottom center",
            onToggle: function (self) { if (self.isActive) setChapter(i); }
          });
          var lead = g.querySelector(".yss-shot--a");
          var trail = g.querySelector(".yss-shot--b");
          if (trail) {
            gsap.fromTo(trail, { y: 90 }, {
              y: -90,
              ease: "none",
              scrollTrigger: { trigger: g, start: "top bottom", end: "bottom top", scrub: true }
            });
          }
          var leadImg = lead && lead.querySelector("img");
          if (leadImg) {
            gsap.fromTo(leadImg, { scale: 1.14 }, {
              scale: 1,
              ease: "none",
              scrollTrigger: { trigger: g, start: "top bottom", end: "center center", scrub: true }
            });
          }
        });
      }, section);
      ctxList.push(ctx);
      return function () { ctx.revert(); };
    });

    mm.add("(max-width: 989px)", function () {
      var ctx = gsap.context(function () {}, section);
      groups.forEach(function (g) {
        revealIn(ctx, g, g.querySelectorAll(".yss-cap, .yss-shot"), { y: 30, duration: 0.7 });
      });
      ctxList.push(ctx);
      return function () { ctx.revert(); };
    });
  }

  function initTestimonials(section, ctxList) {
    reveals(section, ctxList);
    var ctx = gsap.context(function () {}, section);
    revealIn(ctx, section.querySelector(".yts-track"), section.querySelectorAll(".yts-card"), { y: 40 });
    ctxList.push(ctx);
  }

  var initters = {
    "testimonials": initTestimonials,
    "time-back": initTimeBack,
    "brand-panels": initBrandPanels,
    "season-scroll": initSeasonScroll
  };

  function initVideoHero(section, ctxList) {
    reveals(section, ctxList);
    var media = section.querySelector(".yvh__video");
    if (!media) return;
    if (window.matchMedia("(max-width: 989px), (hover: none)").matches) return;
    var ctx = gsap.context(function () {
      gsap.fromTo(media, { scale: 1.12 }, { scale: 1, ease: "none", scrollTrigger: { trigger: section, start: "top bottom", end: "bottom top", scrub: true } });
    }, section);
    ctxList.push(ctx);
  }

  function initMosaic(section, ctxList) {
    reveals(section, ctxList);
    var ctx = gsap.context(function () {}, section);
    revealIn(ctx, section.querySelector(".ymo__grid"), section.querySelectorAll("[data-yap-tile]"), { y: 40, duration: 0.8, stagger: 0.06 });
    ctxList.push(ctx);
  }

  initters["video-hero"] = initVideoHero;
  initters["mosaic"] = initMosaic;

  function initSection(section) {
    var kind = section.getAttribute("data-yap-section");
    if (registry.has(section)) return;
    var ctxList = [];
    if (initters[kind]) initters[kind](section, ctxList);
    else reveals(section, ctxList);
    registry.set(section, ctxList);
  }

  function destroySection(section) {
    var list = registry.get(section);
    if (!list) return;
    list.forEach(function (c) { c.revert(); });
    registry.delete(section);
  }

  function boot(root) {
    (root || document).querySelectorAll("[data-yap-section]").forEach(initSection);
    initProductGrids(root);
  }

  boot(document);
  window.addEventListener("load", function () { ST.refresh(); });
  if (!still && "ResizeObserver" in window) {
    var lastH = 0, t;
    new ResizeObserver(function () {
      var h = document.body.scrollHeight;
      if (Math.abs(h - lastH) < 40) return;
      lastH = h;
      clearTimeout(t);
      t = setTimeout(function () { ST.refresh(); }, 200);
    }).observe(document.body);
  }

  /* Theme editor support */
  document.addEventListener("shopify:section:load", function (e) {
    boot(e.target);
    ST.refresh();
  });
  document.addEventListener("shopify:section:unload", function (e) {
    e.target.querySelectorAll("[data-yap-section]").forEach(destroySection);
    ST.refresh();
  });
})();
